import React, { useEffect, useState } from 'react';
import { CheckCircle, DollarSign, Calendar, Heart, Edit2, Check, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CategoryChecklist } from './CategoryChecklist.tsx';
import { getTasks } from '../../../utils/tasks.ts'; // Adjust the import path as necessary
import { Task } from '../kanban/types.ts';
import { getBudget } from '../../../utils/budget.ts';
import { updateTask } from '../../../utils/tasks.ts'
import { UserProfileAPI } from '../../../services/api/userProfileAPI';
import { getAuth } from 'firebase/auth';
import { Vendor, fetchVendors, organizeVendorsByType } from '../../../types/vendor.ts';

interface Category {
  id: string;
  title: string;
  icon: string;
  tasks: Task[];
}

export const categories: Category[] = [
  {
    id: 'venue',
    title: 'Venue & Ceremony',
    icon: '🏰',
    tasks: [] // Initially empty
  },
  {
    id: 'website',
    title: 'Wedding Website',
    icon: '💻',
    tasks: [] // Initially empty
  },
  {
    id: 'photo',
    title: 'Photos & Videos',
    icon: '📸',
    tasks: [] // Initially empty
  },
  {
    id: 'food',
    title: 'Food & Drink',
    icon: '🍽️',
    tasks: [] // Initially empty
  },
  {
    id: 'attire',
    title: 'Attire & Accessories',
    icon: '👗',
    tasks: [] // Initially empty
  },
  {
    id: 'music',
    title: 'Music & Entertainment',
    icon: '🎵',
    tasks: [] // Initially empty
  },
  {
    id: 'flowers',
    title: 'Flowers & Decor',
    icon: '💐',
    tasks: [] // Initially empty
  },
  {
    id: 'invitations',
    title: 'Invitations & Paper',
    icon: '✉️',
    tasks: [] // Initially empty
  },
  {
    id: 'planning',
    title: 'Planning & Admin',
    icon: '📋',
    tasks: [] // Initially empty
  },
  {
    id: 'honeymoon',
    title: 'Honeymoon',
    icon: '✈️',
    tasks: [] // Initially empty
  }
];

// Add keyframe animations
const glowingText = `
  @keyframes gradientGlow {
    0% {
      background-position: 0% 50%;
      text-shadow: 0px 0px 12px rgba(255, 127, 142, 0.6);
      opacity: 1;
    }
    50% {
      background-position: 100% 50%;
      text-shadow: 0px 0px 20px rgba(255, 214, 182, 0.8);
      opacity: 0.9;
    }
    100% {
      background-position: 0% 50%;
      text-shadow: 0px 0px 15px rgba(231, 216, 240, 0.7);
      opacity: 1;
    }
  }

  @keyframes countdownPulse {
    0% {
      transform: scale(1);
      text-shadow: 0px 0px 20px rgba(255, 105, 180, 0.4);
    }
    50% {
      transform: scale(1.02);
      text-shadow: 0px 0px 30px rgba(255, 105, 180, 0.6);
    }
    100% {
      transform: scale(1);
      text-shadow: 0px 0px 20px rgba(255, 105, 180, 0.4);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  .gradient-text {
    background: linear-gradient(135deg, #FF69B4, #FF7F50, #DDA0DD, #FF69B4);
    background-size: 300% 300%;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: gradientGlow 3s infinite ease-in-out;
  }

  .countdown-number {
    background: linear-gradient(135deg, #FF69B4, #FF7F50, #DDA0DD);
    background-size: 300% 300%;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: gradientGlow 3s infinite ease-in-out, countdownPulse 4s infinite ease-in-out;
  }

  .shimmer-effect {
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    background-size: 200% 100%;
    animation: shimmer 3s infinite;
  }
`;

export const WeddingPlanSummary: React.FC = () => {
  const navigate = useNavigate();
  const [updatedCategories, setUpdatedCategories] = useState(categories);
  const [completedTasks, setCompletedTasks] = useState<Task[]>([]);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [totalTasks, setTotalTasks] = useState(0); // State to hold total tasks
  const [budget, setBudget] = useState(0); // State to hold budget
  
  // Wedding countdown states
  const [userData, setUserData] = useState<any>(null);
  const [weddingDate, setWeddingDate] = useState<string>('');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [editedDate, setEditedDate] = useState<string>('');
  const [daysUntilWedding, setDaysUntilWedding] = useState<number | null>(null);
  const [vendorData, setVendorData] = useState<Record<string, Vendor[]>>({});
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);
  
  const overallProgress = totalTasks === 0 ? 0 : Math.round((completedTasks.length / totalTasks) * 100);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      const auth = getAuth();
      
      if (auth.currentUser && auth.currentUser.email) {
        try {
          const response = await UserProfileAPI.getUserProfile(auth.currentUser.email);
          
          if (response) {
            setUserData(response);
            if (response.weddingDate) {
              setWeddingDate(response.weddingDate);
              setEditedDate(response.weddingDate.split('T')[0]); // Format for input field
              
              // Calculate days until wedding
              const weddingDateTime = new Date(response.weddingDate);
              const today = new Date();
              const timeDiff = weddingDateTime.getTime() - today.getTime();
              const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
              setDaysUntilWedding(daysDiff);
            }
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
        }
      }
    };

    fetchUserProfile();
  }, []);

  // Fetch vendors for subtle display
  useEffect(() => {
    const fetchVendorData = async () => {
      setIsLoadingVendors(true);
      try {
        const vendors = await fetchVendors();
        const organizedVendors = organizeVendorsByType(vendors);
        setVendorData(organizedVendors);
      } catch (error) {
        console.error('Error fetching vendors:', error);
      } finally {
        setIsLoadingVendors(false);
      }
    };

    fetchVendorData();
  }, []);

  // Get relevant vendors for a category based on user location
  const getVendorsForCategory = (categoryId: string): Vendor[] => {
    if (!userData?.weddingLocation || Object.keys(vendorData).length === 0) {
      return [];
    }

    const userLocation = userData.weddingLocation.toLowerCase();
    
    // Map wedding plan categories to vendor categories
    const categoryMapping: Record<string, string[]> = {
      'venue': ['Venues'],
      'photo': ['Photography'],
      'flowers': ['Florists', 'Decor'],
      'food': ['Catering'],
      'attire': ['Unique'], // May include bridal/groom services
      'music': ['Entertainment'],
      'invitations': ['Unique'],
      'planning': ['Wedding Planner'],
      'website': ['Unique'],
      'honeymoon': ['Transport', 'Unique']
    };

    const vendorCategories = categoryMapping[categoryId] || [];
    let relevantVendors: Vendor[] = [];

    vendorCategories.forEach(category => {
      if (vendorData[category]) {
        relevantVendors = [...relevantVendors, ...vendorData[category]];
      }
    });

    // Filter by location proximity (exact match, city, or state)
    const locationFilteredVendors = relevantVendors.filter(vendor => {
      if (!vendor.location) return false;
      
      const vendorLocation = vendor.location.toLowerCase();
      
      // Exact match
      if (vendorLocation.includes(userLocation)) return true;
      
      // Check if user location is within vendor location (e.g., "Austin" in "Austin, TX")
      if (userLocation.split(',')[0].trim() && vendorLocation.includes(userLocation.split(',')[0].trim())) return true;
      
      // Check state match for broader coverage
      const userState = userLocation.includes(',') ? userLocation.split(',').pop()?.trim() : '';
      const vendorState = vendorLocation.includes(',') ? vendorLocation.split(',').pop()?.trim() : '';
      if (userState && vendorState && userState === vendorState) return true;
      
      return false;
    });

    // Sort by rating and verified status, then take top 2
    return locationFilteredVendors
      .sort((a, b) => {
        const aScore = (a.verified ? 2 : 0) + (a.topVendor ? 1 : 0) + (a.rating || 0);
        const bScore = (b.verified ? 2 : 0) + (b.topVendor ? 1 : 0) + (b.rating || 0);
        return bScore - aScore;
      })
      .slice(0, 2);
  };

  // Handle date editing
  const handleEditDate = () => {
    setIsEditingDate(true);
  };

  const handleSaveDate = async () => {
    const auth = getAuth();
    
    if (!editedDate) {
      setIsEditingDate(false);
      return;
    }

    if (!auth.currentUser) {
      console.error('No authenticated user found');
      return;
    }

    try {
      const updatedProfile = await UserProfileAPI.updateUserProfile(
        auth.currentUser.uid,
        {
          ...userData,
          weddingDate: editedDate
        }
      );
      
      setUserData(updatedProfile);
      setWeddingDate(editedDate);
      setIsEditingDate(false);
      
      // Recalculate countdown
      const weddingDateTime = new Date(editedDate);
      const today = new Date();
      const timeDiff = weddingDateTime.getTime() - today.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      setDaysUntilWedding(daysDiff);
    } catch (error) {
      console.error("Error updating wedding date:", error);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedDate(e.target.value);
  };

  const formatCountdownText = (days: number) => {
    if (days > 0) {
      return `${days} days to go!`;
    } else if (days === 0) {
      return "Your wedding is today! 🎉";
    } else {
      return `${Math.abs(days)} days since your wedding ❤️`;
    }
  };

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const budgetSections = await getBudget();
        const totalCost = budgetSections.reduce((acc, item) => acc + (item.cost || 0), 0);
        setBudget(totalCost)
        const tasks = await getTasks();
        if (tasks && tasks.length > 0) {
          const completedTasks = tasks.filter(task => task.status === 'DONE');
          setCompletedTasks(completedTasks);
          // Map tasks to categories
          const tasksByCategory = updatedCategories.map(category => {
            const categoryTasks = tasks
              .filter(task => task.taskBoard.toLowerCase().includes(category.id.toLowerCase()))
            return { ...category, tasks: categoryTasks }; // Update category with task names
          });
          setUpdatedCategories(tasksByCategory); // Update state with tasks

          // Set totalTasks based on the length of tasks
          const total = tasks.length; // Get the total number of tasks
          setTotalTasks(total); // Update the totalTasks state
        }
      } catch (error) {
        console.error('Error fetching tasks:', error);
      }
    };

    fetchTasks(); // Call the async function
  }, []); // Empty dependency array to run only on mount

  useEffect(() => {
    // Add the styles to the document
    const styleSheet = document.createElement("style");
    styleSheet.innerText = glowingText;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const toggleTask = async (task: Task, isCompleted: boolean) => {
    const updatedTask: Task = {
      id: task.id,
      name: task.name,
      orderIndex: task.orderIndex,
      description: task.description || '',
      priority: task.priority || 'MEDIUM',
      dueDate: task.dueDate || '',
      taskBoard: task.taskBoard,
      status: isCompleted ? 'DONE' : 'TODO'
    };

    await updateTask(task.id, updatedTask)
    setCompletedTasks(prev => 
      prev.includes(task) 
        ? prev.filter(t => t !== task)
        : [...prev, task]
    );
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <div className="mx-auto mt-16 space-y-8">
      
            {/* Wedding Countdown - Enhanced Comprehensive Display */}
      <div className="relative mb-8 px-2 sm:px-0">
        <div className="relative backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-white/70 overflow-hidden">
          {/* Enhanced background elements */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-pink-200/30 to-rose-300/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-purple-200/20 to-pink-200/15 rounded-full blur-2xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-rose-100/10 to-pink-100/10 rounded-full blur-3xl"></div>
          
          <div className="relative z-10">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8">
              <div className="flex items-center gap-4 mb-4 sm:mb-0">
                <div className="p-4 bg-gradient-to-br from-pink-100/90 to-rose-100/90 backdrop-blur-sm rounded-2xl border border-white/70 shadow-lg">
                  {weddingDate && daysUntilWedding !== null && daysUntilWedding >= 0 ? (
                    <Heart className="w-8 h-8 text-pink-600" />
                  ) : (
                    <Calendar className="w-8 h-8 text-pink-600" />
                  )}
                </div>
                <div>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
                    {weddingDate && daysUntilWedding !== null ? (
                      daysUntilWedding > 0 ? "Wedding Countdown" 
                      : daysUntilWedding === 0 ? "Your Special Day!" 
                      : "Wedding Memories"
                    ) : "Wedding Planning"}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {weddingDate && daysUntilWedding !== null ? (
                      daysUntilWedding > 0 ? "Time until your big day"
                      : daysUntilWedding === 0 ? "Today is the day! 🎉"
                      : "Cherished memories from your special day"
                    ) : "Set your wedding date to begin"}
                  </p>
                </div>
              </div>

              {/* Edit Date Section */}
              <div className="flex-shrink-0">
                {isEditingDate ? (
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border border-white/70 shadow-lg min-w-[280px]">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Select Your Wedding Date</label>
                    <input
                      type="date"
                      value={editedDate}
                      onChange={handleDateChange}
                      className="w-full bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 text-gray-800 border border-pink-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 text-center text-lg font-medium"
                    />
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={handleSaveDate}
                        className="flex-1 py-3 px-4 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        <Check className="w-4 h-4 inline mr-2" />
                        Save Date
                      </button>
                      <button
                        onClick={() => setIsEditingDate(false)}
                        className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-all duration-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={handleEditDate}
                    className="group py-3 px-6 bg-white/90 hover:bg-white backdrop-blur-sm border border-white/70 hover:border-pink-200 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <div className="flex items-center gap-3">
                      <Edit2 size={18} className="text-pink-600 group-hover:text-pink-700" />
                      <span className="font-semibold text-gray-700 group-hover:text-gray-800">
                        {weddingDate ? 'Edit Date' : 'Set Wedding Date'}
                      </span>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {weddingDate ? (
              <>
                {/* Wedding Date Display */}
                <div className="text-center mb-8">
                  <div className="inline-block bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/60 shadow-lg">
                    <div className="text-lg font-semibold text-gray-700 mb-2">Your Wedding Day</div>
                    <div className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
                      {new Date(weddingDate).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'long', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    {userData?.weddingLocation && (
                      <div className="flex items-center justify-center gap-2 mt-3 text-gray-600">
                        <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                        <span className="text-sm">{userData.weddingLocation}</span>
                      </div>
                    )}
                  </div>
                </div>

                {daysUntilWedding !== null && (
                  <>
                    {/* Countdown Display */}
                    {daysUntilWedding > 0 ? (
                      <div className="space-y-6">
                        {/* Time Units Grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                          {(() => {
                            const months = Math.floor(daysUntilWedding / 30);
                            const weeks = Math.floor((daysUntilWedding % 30) / 7);
                            const days = daysUntilWedding % 7;
                            const hoursLeft = 24 - new Date().getHours();

                            return [
                              { label: 'Months', value: months, color: 'from-pink-500 to-rose-500' },
                              { label: 'Weeks', value: weeks, color: 'from-purple-500 to-pink-500' },
                              { label: 'Days', value: days, color: 'from-rose-500 to-orange-500' },
                              { label: 'Hours Today', value: hoursLeft, color: 'from-orange-500 to-yellow-500' }
                            ].map((unit, index) => (
                              <div key={unit.label} className="group">
                                <div className={`bg-gradient-to-br ${unit.color} p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-white/20`}>
                                  <div className="text-center">
                                    <div className="text-3xl lg:text-4xl font-black text-white mb-2">
                                      {unit.value}
                                    </div>
                                    <div className="text-white/90 font-medium text-sm uppercase tracking-wider">
                                      {unit.label}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>

                     
                        {/* Status Messages */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          
                          
                        
                        </div>
                      </div>
                    ) : daysUntilWedding === 0 ? (
                      // Wedding Day
                      <div className="text-center py-12">
                        <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-3xl p-8 shadow-2xl border border-white/30">
                          <div className="text-6xl mb-4">🎉</div>
                          <div className="text-4xl font-black text-white mb-4">Today is the Day!</div>
                          <div className="text-xl text-white/90 mb-6">Wishing you a magical wedding day filled with love and joy!</div>
                          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 inline-block">
                            <div className="text-white/90 text-lg font-semibold">Congratulations! 💕</div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Past Wedding
                      <div className="text-center py-8">
                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl p-8 shadow-2xl border border-white/30">
                          <div className="text-4xl mb-4">💕</div>
                          <div className="text-3xl font-black text-white mb-4">Wedding Memories</div>
                          <div className="text-lg text-white/90 mb-4">
                            {Math.abs(daysUntilWedding)} {Math.abs(daysUntilWedding) === 1 ? 'day' : 'days'} of beautiful memories
                          </div>
                          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 inline-block">
                            <div className="text-white/90 font-semibold">Hope it was everything you dreamed! ✨</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              // No Date Set
              <div className="text-center py-12">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-white/60 shadow-lg max-w-md mx-auto">
                  <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-6" />
                  <div className="text-2xl font-bold text-gray-700 mb-4">Set Your Wedding Date</div>
                  <div className="text-gray-600 mb-6">
                    Start your countdown and unlock personalized planning timelines, vendor recommendations, and more!
                  </div>
                  <button
                    onClick={handleEditDate}
                    className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Choose Your Date
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 px-2 sm:px-0">

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-200/60">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-50 rounded-xl">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Overall Progress</span>
                <div className="text-2xl font-bold text-gray-900">{overallProgress}%</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-100 rounded-full h-3">
            <div 
              className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-200/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-50 rounded-xl">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Total Budget</span>
                <div className="text-2xl font-bold text-gray-900">${budget.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>

      
      </div>

      <div className="space-y-6 mb-8 sm:mb-24">
        {updatedCategories.map((category) => (
          <CategoryChecklist
            key={category.id}
            category={category}
            isExpanded={expandedCategory === category.id}
            onToggle={() => toggleCategory(category.id)}
            completedTasks={completedTasks}
            onToggleTask={toggleTask}
            vendors={getVendorsForCategory(category.id)}
            userLocation={userData?.weddingLocation}
            isLoadingVendors={isLoadingVendors}
          />
        ))}
      </div>

      <div className="h-12 sm:h-24" />
    </div>
  );
  };
  
  export default WeddingPlanSummary;